import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Printer, Download } from "lucide-react";

function fmt(v, d = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return typeof v === "number" ? v.toFixed(d) : v;
}
function fmtPower(p) {
  if (p === null || p === undefined) return "—";
  const s = p >= 0 ? "+" : "";
  return `${s}${p.toFixed(2)} D`;
}

function EyeReport({ side, req, data }) {
  if (!data) return null;
  const sel = data.formula_selection;
  const eye = side === "OD" ? req.od : req.os;
  const ct = data.cristalens_toric;
  const at = data.artis_toric;
  return (
    <div className="rounded-lg border border-slate-300 bg-white p-4 print:p-0 print:border-0">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-serif text-xl font-semibold">{side === "OD" ? "OD · Right Eye" : "OS · Left Eye"}</div>
        <div className="text-xs text-slate-500 font-mono">A-const {data.a_constant}{data.a_constant_other && data.a_constant_other !== data.a_constant ? ` / ${data.a_constant_other}` : ""}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Biometry</div>
          <div className="font-mono text-xs mt-1 space-y-0.5">
            <div>K1 {fmt(eye?.k1)} D @ {fmt(eye?.k1_axis, 0)}°</div>
            <div>K2 {fmt(eye?.k2)} D @ {fmt(eye?.k2_axis, 0)}°</div>
            <div>Avg K {fmt(data.avg_k)} D · Cyl {fmt(data.corneal_astigmatism)} D</div>
            <div>AL {fmt(eye?.al)} · ACD {fmt(eye?.acd)} · LT {fmt(eye?.lt)} · WTW {fmt(eye?.wtw)}</div>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Formula</div>
          <div className="font-serif text-lg font-semibold text-sky-800">{sel.formula}</div>
          <div className="text-xs text-slate-600 mt-1 leading-snug">{sel.reason}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Spherical IOL Power</div>
          <div className="mt-1 space-y-0.5 text-sm">
            <div>Emmetropia: <span className="font-mono font-semibold">{fmtPower(data.iol_power_emmetropia)}</span></div>
            {data.elp && <div>ELP: <span className="font-mono">{data.elp.used.toFixed(2)} mm</span> <span className="text-slate-500">(anat {data.elp.anatomical.toFixed(2)} / K-AL {data.elp.k_al.toFixed(2)})</span></div>}
            <div>Target {req.target_refraction > 0 ? "+" : ""}{req.target_refraction?.toFixed?.(2)} D: <span className="font-mono font-semibold">{fmtPower(data.iol_power_at_target)}</span></div>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Toric — Cristalens</div>
          <div className="text-xs mt-1">
            {ct.recommended_model} · align <span className="font-mono">{fmt(ct.iol_alignment_axis, 1)}°</span> · IOL cyl <span className="font-mono">{fmt(ct.recommended_iol_cyl)} D</span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-2">Toric — Artis (PCA)</div>
          <div className="text-xs mt-1">
            {at.recommended_model} · align <span className="font-mono">{fmt(at.iol_alignment_axis, 1)}°</span> · IOL cyl <span className="font-mono">{fmt(at.recommended_iol_cyl)} D</span> · residual <span className="font-mono">{fmt(at.residual_astigmatism)} D</span>
          </div>
          <div className="text-xs mt-1 text-slate-600">Placement: {at.iol_placement}</div>
        </div>
      </div>
    </div>
  );
}

export default function ReportCard({ result, request, onPrint, onDownloadPdf, downloading = false, printMode = false }) {
  return (
    <Card className={`p-5 sm:p-6 border-slate-300 ${printMode ? "print-report" : ""}`} data-testid={printMode ? "report-card-print" : "report-card"}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-sky-700" />
          <h2 className="font-serif text-2xl font-semibold tracking-tight">Clinical Summary Report</h2>
        </div>
        {!printMode && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onPrint} data-testid="print-report-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button
              size="sm"
              onClick={onDownloadPdf}
              disabled={downloading}
              data-testid="download-pdf-btn"
              className="bg-sky-700 hover:bg-sky-800 text-white"
            >
              <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div><span className="text-slate-500 text-xs uppercase tracking-wider mr-2">Patient</span><span className="font-medium">{request.patient_name || "—"}</span></div>
        <div><span className="text-slate-500 text-xs uppercase tracking-wider mr-2">ID</span><span className="font-mono">{request.patient_id || "—"}</span></div>
        <div><span className="text-slate-500 text-xs uppercase tracking-wider mr-2">Age</span><span>{request.age || "—"}</span></div>
        <div><span className="text-slate-500 text-xs uppercase tracking-wider mr-2">Prior surgery</span><span>{request.prior_surgery?.replace(/_/g, " ") || "none"}</span></div>
        <div className="col-span-2"><span className="text-slate-500 text-xs uppercase tracking-wider mr-2">IOL model</span><span data-testid="report-lens-name">{request.lens_name || "Custom / manual A-const"}</span></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EyeReport side="OD" req={request} data={result.od} />
        <EyeReport side="OS" req={request} data={result.os} />
      </div>
      {result.al_difference !== undefined && (
        <div className="mt-4 text-xs text-slate-600">
          AL difference OD/OS: <span className="font-mono">{result.al_difference?.toFixed?.(2)} mm</span>{" "}
          {result.al_difference >= 0.2 && <span className="text-amber-700 font-semibold">— exceeds 0.2 mm clinical threshold</span>}
        </div>
      )}
      {result.warnings?.filter((w) => w.includes("rotation guard")).map((w) => (
        <div key={w} className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="report-rotation-guard">
          {w}
        </div>
      ))}
      <div className="mt-6 grid grid-cols-2 gap-6 text-xs text-slate-500">
        <div>
          <div className="border-t border-slate-300 pt-2">Surgeon signature</div>
        </div>
        <div>
          <div className="border-t border-slate-300 pt-2">Date</div>
        </div>
      </div>
      <div className="mt-4 text-[10px] text-slate-400 leading-snug">
        Barrett Universal II (approximation, A-constant {request.a_constant}) is the headline power; SRK/T, Haigis,
        Hoffer Q, Holladay 1 use their published equations{request.a_constant_other && request.a_constant_other !== request.a_constant ? ` with A-constant ${request.a_constant_other}` : ""}.
        Barrett True-K adjustment for post-refractive eyes, Cristalens toric ladder with SIA {request.sia} D @ {request.sia_axis}° temporal,
        and Artis toric with Abulafia-Koch posterior corneal astigmatism regression. Clinical decision support only —
        verify against the official ESCRS / Barrett calculators prior to implantation.
      </div>
    </Card>
  );
}
